(function () {
  'use strict';

  var EXPERIENCE_ORDER = ['Beginner', 'Intermediate', 'Advanced'];
  var FIRMNESS_ORDER = ['Soft', 'Medium', 'Firm', 'Rigid'];
  var SIZE_ORDER = ['compact', 'medium', 'large'];

  var LOADING_MESSAGES = [
    'Locating your best matches for total pleasure...',
    'Consulting the pleasure algorithms...',
    'Cross-referencing your desires...',
    'Reticulating curves...',
    'Calculating body fit chemistry...',
    'Aligning stars (and curves)...',
    'Measuring twice, matching once...',
    'Warming things up...',
    'Summoning your perfect match...',
    'Finding your happy place...',
    'Sizing up the possibilities...',
    'Getting a feel for your preferences...',
    'Shuffling the perfect shortlist...',
    'Polishing your top picks...',
    'Double-checking the vibe...'
  ];

  // Minimum "best possible score" before a % match badge is shown at all.
  // 45 is roughly two meaningful answers — enough for the number to vary
  // between products instead of everything tying on 100%.
  var MATCH_BADGE_MIN_SCORE = 45;

  // UTM tags appended to every product link clicked from the results grid.
  // The click never leaves regulation.co.uk, but tagging it still lets it
  // show up as its own campaign in analytics instead of blending into
  // ordinary collection-page traffic. Source/medium are this quiz's own
  // values; campaign is the name Pete gave this feature. Change only here —
  // nothing else in the file references the individual pieces.
  var CAMPAIGN_UTM = 'utm_source=toymatch-quiz&utm_medium=quiz&utm_campaign=toy-match-maker';

  function sizeBand(lengthCm) {
    if (lengthCm === null || lengthCm === undefined) return null;
    if (lengthCm < 12) return 'compact';
    if (lengthCm <= 18) return 'medium';
    return 'large';
  }

  function computeScore(v, answers) {
    if (answers.flaredBase === 'required' && !v.flaredBase) return null;
    if (answers.vibration === 'no' && v.vibration) return null;

    var score = 0;
    var reasons = [];

    if (answers.experience) {
      var ai = EXPERIENCE_ORDER.indexOf(answers.experience);
      var vi = EXPERIENCE_ORDER.indexOf(v.experienceLevel);
      if (ai > -1 && vi > -1) {
        var d = Math.abs(ai - vi);
        if (d === 0) {
          score += 30;
          reasons.push(v.experienceLevel);
        } else if (d === 1) {
          score += 10;
        }
      }
    }

    // Firmness allows more than one pick, so this scores against whichever
    // selected value the variant sits closest to rather than a single
    // distance calculation. An exact match against any selected value still
    // wins outright even if a less-close pick was selected too — picking
    // both "Soft" and "Rigid" shouldn't cap a Soft variant's score at the
    // near-miss rate just because Rigid was also on the list.
    if (answers.firmness && answers.firmness.length) {
      var vfi = FIRMNESS_ORDER.indexOf(v.firmness);
      var firmnessBest = null;
      var firmnessExact = false;
      answers.firmness.forEach(function (choice) {
        var fi = FIRMNESS_ORDER.indexOf(choice);
        if (fi === -1 || vfi === -1) return;
        var fd = Math.abs(fi - vfi);
        var pts = fd === 0 ? 25 : fd === 1 ? 10 : 2;
        if (firmnessBest === null || pts > firmnessBest) firmnessBest = pts;
        if (fd === 0) firmnessExact = true;
      });
      if (firmnessBest !== null) {
        score += firmnessBest;
        if (firmnessExact) reasons.push(v.firmness + ' firmness');
      }
    }

    (answers.intendedUse || []).forEach(function (tag) {
      if (v.intendedUse && v.intendedUse.indexOf(tag) > -1) {
        score += 15;
        reasons.push(tag);
      }
    });

    (answers.toyType || []).forEach(function (tag) {
      if (v.toyType && v.toyType.indexOf(tag) > -1) {
        score += 15;
        reasons.push(tag);
      }
    });

    if (answers.size && answers.size !== 'no-preference') {
      var band = sizeBand(v.insertableLengthCm);
      if (band) {
        var bi = SIZE_ORDER.indexOf(answers.size);
        var vbi = SIZE_ORDER.indexOf(band);
        if (bi === vbi) {
          score += 20;
          reasons.push(band + ' size');
        } else if (Math.abs(bi - vbi) === 1) {
          score += 8;
        }
      }
    }

    (answers.texture || []).forEach(function (tag) {
      if (v.texture && v.texture.indexOf(tag) > -1) {
        score += 8;
        reasons.push(tag + ' texture');
      }
    });

    if (answers.material && answers.material.length && v.material &&
        answers.material.indexOf(v.material) > -1) {
      score += 12;
      reasons.push(v.material);
    }

    if (answers.vibration === 'yes' && v.vibration) {
      score += 15;
      reasons.push('Vibrating');
    }
    if (answers.harness === 'yes' && v.harnessCompatible) {
      score += 15;
      reasons.push('Harness compatible');
    }
    if (answers.waterproof === 'yes' && v.waterproof) {
      score += 10;
      reasons.push('Waterproof');
    }

    return { score: score, reasons: reasons };
  }

  // Highest score a product could realistically reach given these answers, used
  // as the denominator for the "% match" badge. Multi-select answers are capped
  // at two matches because almost no variant carries more than two tags in a
  // single list — counting all five of someone's texture picks would deflate
  // every percentage on the page and make good matches look poor.
  function maxAchievableScore(answers) {
    var max = 0;
    var capped = function (arr, weight) {
      var n = Math.min((arr || []).length, 2);
      return n * weight;
    };
    if (answers.experience) max += 30;
    // Firmness's ceiling is still a single 25, same as before — selecting
    // more values widens the odds of a match, it doesn't raise the cap.
    if (answers.firmness && answers.firmness.length) max += 25;
    if (answers.size && answers.size !== 'no-preference') max += 20;
    max += capped(answers.intendedUse, 15);
    max += capped(answers.toyType, 15);
    max += capped(answers.texture, 8);
    if (answers.material && answers.material.length) max += 12;
    if (answers.vibration === 'yes') max += 15;
    if (answers.harness === 'yes') max += 15;
    if (answers.waterproof === 'yes') max += 10;
    return max;
  }

  // Turns whatever was typed into the optional name field into a possessive
  // for the results heading, or an empty string if there's nothing usable —
  // in which case every caller falls back to the original wording.
  //
  // The tidying is deliberate rather than fussy: this string is written
  // straight into the page, so it takes the first word only ("Ben Smith"
  // reads better as "Ben's"), caps the length, and forces a capital first
  // letter so a lowercase "ben" doesn't produce "ben's matches". Names ending
  // in s get "James's", which is the current British convention.
  //
  // Note the value is only ever assigned via textContent, never innerHTML,
  // so there is no markup path out of this function.
  function possessiveName(raw) {
    var name = String(raw || '').replace(/\s+/g, ' ').trim();
    if (!name) return '';
    name = name.split(' ')[0].slice(0, 20);
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1) + "'s";
  }

  function getResults(variants, answers) {
    var byProduct = {};
    variants.forEach(function (v) {
      var r = computeScore(v, answers);
      if (!r) return;
      var existing = byProduct[v.productId];
      if (!existing || r.score > existing.score) {
        var merged = {};
        for (var k in v) { if (Object.prototype.hasOwnProperty.call(v, k)) merged[k] = v[k]; }
        merged.score = r.score;
        merged.reasons = r.reasons;
        byProduct[v.productId] = merged;
      }
    });
    var results = Object.keys(byProduct).map(function (id) { return byProduct[id]; });
    results.sort(function (a, b) { return b.score - a.score; });
    return results;
  }

  function money(cents) {
    var value = (Number(cents) / 100).toFixed(2);
    return '£' + value;
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function cmToInches(cm) {
    return cm / 2.54;
  }

  function formatDimension(cm) {
    if (cm === null || cm === undefined) return null;
    var inches = cmToInches(cm);
    return Number(cm).toFixed(1) + 'cm / ' + inches.toFixed(1) + '"';
  }

  function pickTwoMessages() {
    var pool = LOADING_MESSAGES.slice();
    var first = pool.splice(Math.floor(Math.random() * pool.length), 1)[0];
    var second = pool.length ? pool.splice(Math.floor(Math.random() * pool.length), 1)[0] : first;
    return [first, second];
  }

  // Builds checkbox options for a multi-value field from whatever values
  // actually exist across the collection's variants. Skips any value that
  // appears on every single variant, since a checkbox that matches
  // everything doesn't help anyone narrow anything down.
  function buildMultiOptions(containerEl, variants, fieldName) {
    if (!containerEl) return 0;
    var total = variants.length;
    var counts = {};
    variants.forEach(function (v) {
      var arr = v[fieldName] || [];
      var seen = {};
      arr.forEach(function (val) {
        if (!val || seen[val]) return;
        seen[val] = true;
        counts[val] = (counts[val] || 0) + 1;
      });
    });
    var values = Object.keys(counts).filter(function (val) {
      return counts[val] < total;
    });
    values.sort();
    values.forEach(function (val) {
      var label = document.createElement('label');
      label.className = 'toymatch-quiz__option';
      label.innerHTML = '<input type="checkbox" name="' + fieldName + '" value="' + escapeHtml(val) + '"><span>' + escapeHtml(val) + '</span>';
      containerEl.appendChild(label);
    });
    return values.length;
  }

  // Liquid caps collection.products at 50 per render, and when this section
  // sits after the product grid it inherits that grid's pagination — so the
  // inline JSON blob only ever holds a fraction of a large collection.
  // The `toymatch-data` template renders the same JSON with no layout,
  // paginated by 50. Fetch page 1, read paginate.pages, then pull the rest in
  // parallel and stitch them together.
  function fetchFullCatalogue() {
    var params = new URLSearchParams(window.location.search);
    var preview = params.get('preview_theme_id');
    var base = window.location.pathname + '?view=toymatch-data' +
      (preview ? '&preview_theme_id=' + encodeURIComponent(preview) : '') + '&page=';

    function getPage(n) {
      return fetch(base + n, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (t) {
          if (!t) return null;
          try { return JSON.parse(t.trim()); } catch (e) { return null; }
        })
        .catch(function () { return null; });
    }

    return getPage(1).then(function (first) {
      if (!first || !first.variants) return null;
      var pages = first.pages || 1;
      if (pages <= 1) return first.variants;
      var rest = [];
      for (var p = 2; p <= pages; p++) rest.push(getPage(p));
      return Promise.all(rest).then(function (results) {
        var all = first.variants.slice();
        results.forEach(function (res) {
          if (res && res.variants) all = all.concat(res.variants);
        });
        return all;
      });
    }).catch(function () { return null; });
  }

  // Measures the loading icon's outline so the CSS "drawing" animation
  // traces the exact path length instead of a guessed dash value.
  function setupSpinner(root) {
    var path = root.querySelector('.toymatch-quiz__spinner-path');
    if (!path || typeof path.getTotalLength !== 'function') return;
    var length = path.getTotalLength();
    path.style.strokeDasharray = length;
    path.style.setProperty('--path-len', length);
  }

  function initQuiz(root) {
    setupSpinner(root);

    var sectionId = root.getAttribute('data-section-id');
    var dataEl = document.getElementById('toymatch-quiz-data-' + sectionId);
    var variants = [];
    try {
      variants = JSON.parse((dataEl && dataEl.textContent) || '[]');
    } catch (e) {
      variants = [];
    }
    variants = variants.filter(function (v) { return v.toyType && v.toyType.length; });

    // Shopify prefixes the section wrapper id with the template id, so match
    // on suffix rather than assuming a fixed "shopify-section-collection-products" id.
    var nativeGrid = document.querySelector('[id$="collection-products"]');
    var nativeBanner = document.querySelector('[id$="collection-banner"]');

    var introEl = root.querySelector('[data-quiz-intro]');
    var formEl = root.querySelector('[data-quiz-form]');
    var loadingEl = root.querySelector('[data-quiz-loading]');
    var loadingMessageEl = root.querySelector('[data-quiz-loading-message]');
    var resultsEl = root.querySelector('[data-quiz-results]');
    var gridEl = root.querySelector('[data-quiz-grid]');
    var progressBar = root.querySelector('[data-quiz-progress-bar]');

    var materialOptionsEl = root.querySelector('[data-material-options]');
    var toyTypeOptionsEl = root.querySelector('[data-toytype-options]');
    var steps = [];
    var optionsBuilt = false;

    // Full-screen takeover while the visitor is answering. The classes are set
    // regardless of screen size — the stylesheet only acts on them below 749px
    // — so there's nothing to re-check on resize or rotate.
    function enterFullscreen() {
      root.classList.add('is-fullscreen');
      document.body.classList.add('toymatch-locked');
      root.scrollTop = 0;
    }

    function exitFullscreen() {
      root.classList.remove('is-fullscreen');
      document.body.classList.remove('toymatch-locked');
    }

    // Dark mode is a section setting. It darkens the whole page for the
    // duration of the quiz and the results.
    var darkOn = root.getAttribute('data-quiz-dark') === 'true';
    var hideExtras = root.getAttribute('data-quiz-dark-hide-extras') === 'true';

    // The drifting background is a real element rather than a body::before,
    // so it can't collide with anything the theme already uses on that
    // pseudo-element. It sits at z-index -1: behind every bit of page content,
    // in front of the body's own background colour.
    var bgLayer = null;

    function enterDark() {
      if (!darkOn) return;
      document.body.classList.add('toymatch-dark');
      if (hideExtras) document.body.classList.add('toymatch-dark-hide-extras');
      if (!bgLayer) {
        bgLayer = document.createElement('div');
        bgLayer.className = 'toymatch-bg';
        bgLayer.setAttribute('aria-hidden', 'true');
        document.body.appendChild(bgLayer);
      }
      // Motion resumes if they retake the quiz after seeing results.
      document.body.classList.remove('toymatch-settled');
    }

    // Nothing calls this any more — the "Show all products instead" link that
    // did was removed from the results screen. Kept because the guarded
    // handlers below still reference it and reinstating either link should be
    // a markup-only change.
    function exitDark() {
      document.body.classList.remove('toymatch-dark');
      document.body.classList.remove('toymatch-dark-hide-extras');
      document.body.classList.remove('toymatch-settled');
      if (bgLayer && bgLayer.parentNode) {
        bgLayer.parentNode.removeChild(bgLayer);
        bgLayer = null;
      }
    }

    // Results are the destination, so the background stops moving. Pausing
    // rather than clearing the animation means it freezes exactly where it is
    // instead of snapping back to the start.
    function settleBackground() {
      document.body.classList.add('toymatch-settled');
    }

    enterFullscreen();
    enterDark();

    // Populate dynamic option lists BEFORE computing the step list, so that
    // a question with nothing useful to ask can be removed from the DOM.
    // Deferred until the full catalogue has loaded so the options reflect
    // every product in the collection, not just the first page.
    function buildOptionsAndSteps() {
      if (optionsBuilt) return;
      optionsBuilt = true;

      // Material is a single value per variant but a multi-select for the
      // visitor, so build it from the distinct materials present rather than
      // reusing buildMultiOptions (which expects an array field).
      if (materialOptionsEl) {
        var materials = [];
        variants.forEach(function (v) {
          if (v.material && materials.indexOf(v.material) === -1) materials.push(v.material);
        });
        materials.sort();
        materials.forEach(function (m) {
          var label = document.createElement('label');
          label.className = 'toymatch-quiz__option';
          label.innerHTML = '<input type="checkbox" name="material" value="' + escapeHtml(m) + '"><span>' + escapeHtml(m) + '</span>';
          materialOptionsEl.appendChild(label);
        });
        // Nothing to choose between means nothing worth asking.
        if (materials.length < 2) {
          var matStep = materialOptionsEl.closest('[data-quiz-step]');
          if (matStep) matStep.remove();
        }
      }

      var toyTypeCount = buildMultiOptions(toyTypeOptionsEl, variants, 'toyType');
      if (toyTypeCount === 0 && toyTypeOptionsEl) {
        var toyTypeStep = toyTypeOptionsEl.closest('[data-quiz-step]');
        if (toyTypeStep) toyTypeStep.remove();
      }

      steps = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-step]'));
    }

    // Kick the fetch off at page load so it's almost always finished by the
    // time the visitor has read the intro and pressed start.
    var catalogueReady = fetchFullCatalogue().then(function (full) {
      if (full && full.length) {
        variants = full.filter(function (v) { return v.toyType && v.toyType.length; });
      }
      buildOptionsAndSteps();
    });

    var backBtn = root.querySelector('[data-quiz-back]');
    var nextBtn = root.querySelector('[data-quiz-next]');
    var finishBtn = root.querySelector('[data-quiz-finish]');
    var startBtn = root.querySelector('[data-quiz-start]');
    var skipLink = root.querySelector('[data-quiz-skip]');
    var showAllLink = root.querySelector('[data-quiz-show-all]');
    var retakeBtn = root.querySelector('[data-quiz-retake]');

    var currentStep = 0;

    // Screen readers get no signal when a step swaps out, so announcements go
    // through a polite live region that sighted users never see.
    var liveRegion = document.createElement('div');
    liveRegion.className = 'toymatch-quiz__sr-only';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    root.appendChild(liveRegion);

    function announce(msg) {
      liveRegion.textContent = '';
      window.setTimeout(function () { liveRegion.textContent = msg; }, 50);
    }

    // A short buzz on selection. Guarded because desktop and iOS Safari don't
    // implement it, and it should never be the only feedback.
    function tap() {
      if (navigator.vibrate) {
        try { navigator.vibrate(10); } catch (e) {}
      }
    }

    function updateProgress() {
      var pct = steps.length ? ((currentStep) / (steps.length - 1)) * 100 : 0;
      if (progressBar) progressBar.style.width = pct + '%';
    }

    // Steps with exactly one radio group and no checkboxes are a single
    // decision, so tapping an answer moves things along without a second tap
    // on Next. Every multi-select step is excluded by the checkbox test —
    // including firmness and must-haves, both of which are checkbox lists now
    // — and so is the name step, since typing must never advance the quiz.
    function isSingleChoiceStep(stepEl) {
      if (!stepEl) return false;
      if (stepEl.querySelector('input[type="checkbox"]')) return false;
      var names = {};
      Array.prototype.forEach.call(
        stepEl.querySelectorAll('input[type="radio"]'),
        function (i) { names[i.name] = true; }
      );
      return Object.keys(names).length === 1;
    }

    /* Next is redundant on a step that advances by itself, so it is hidden
       there — but only while nothing is checked yet. Every option on a step
       with no default is a live button: tapping any of them fires a change
       event and the JS auto-advances, so there's no "leave it as-is" action
       to provide for and Next would just be a second way to do nothing.

       Size is the one step that breaks that assumption — its "No preference"
       radio carries `checked` in the markup, deliberately, as the only
       pre-selected option anywhere in the quiz. Re-tapping an
       already-checked radio fires no change event at all, so if Next were
       hidden there too, a visitor happy with that default would have no way
       to move forward. That was the reported bug: a step landed on with an
       option already selected and no visible way to submit. The fix is
       this function checking for an existing :checked radio rather than
       hiding Next unconditionally — Next shows whenever a single-choice step
       already has an answer, and picking a *different* size still
       auto-advances immediately the same as every other step.

       The other exception is arriving backwards. Re-tapping an
       already-selected radio fires no change event, so a visitor who pressed
       Back to review an answer and wants to keep it would have no way forward
       at all. Next stays available whenever the step was reached with Back.

       The experience step carries no checked default and has no "No
       preference" option, so it always falls into the first case above: it
       has to be tapped before the quiz will move on. It is the highest
       weighted signal in the scoring at 30 points. Reaching it via Back
       always means it is already answered, so that route can't be used to
       skip it. */
    function shouldHideNext(stepEl, direction) {
      if (!isSingleChoiceStep(stepEl)) return false;
      if (direction === 'back') return false;
      return !stepEl.querySelector('input[type="radio"]:checked');
    }

    // Retaking must land back on exactly the same defaults as a first visit:
    // only "No preference" (where one exists, currently just size) checked,
    // every other radio empty — not whatever was picked on the previous run.
    // defaultChecked reflects the `checked` attribute from the original
    // markup regardless of anything a visitor did at runtime, so restoring
    // every radio to it puts each step back at its intended baseline.
    //
    // Scoped to radios specifically. The name field and the multi-select
    // checkbox steps carrying over isn't the bug this fixes, so they're left
    // as the visitor last set them.
    function resetSingleChoiceSteps() {
      Array.prototype.forEach.call(formEl.querySelectorAll('input[type="radio"]'), function (input) {
        input.checked = input.defaultChecked;
      });
    }

    function showStep(index, direction) {
      steps.forEach(function (stepEl, i) {
        stepEl.classList.toggle('is-active', i === index);
        stepEl.classList.remove('is-forward', 'is-back');
      });
      var active = steps[index];
      if (active && direction) active.classList.add(direction === 'back' ? 'is-back' : 'is-forward');

      backBtn.hidden = index === 0;
      var isLast = index === steps.length - 1;
      nextBtn.hidden = isLast || shouldHideNext(active, direction);
      finishBtn.hidden = !isLast;
      updateProgress();

      // A long question followed by a short one would otherwise leave the
      // overlay scrolled halfway down.
      if (root.classList.contains('is-fullscreen')) root.scrollTop = 0;

      if (active) {
        var h = active.querySelector('h3');
        if (h) {
          // Focusing the heading is what actually makes a screen reader read
          // the new question; tabindex -1 keeps it out of the tab order, and
          // the stylesheet suppresses the focus ring it would otherwise draw.
          h.setAttribute('tabindex', '-1');
          h.focus({ preventScroll: true });
        }
        announce('Question ' + (index + 1) + ' of ' + steps.length +
                 (h ? '. ' + h.textContent.trim() : ''));
      }
    }

    function collectAnswers() {
      var answers = {};
      var formData = new FormData(formEl);
      answers.firstName = formData.get('firstName') || '';
      answers.experience = formData.get('experience') || '';
      // Size is still single-choice; "No preference" carries a checked
      // default in the markup, so this fallback only matters if the step is
      // ever somehow skipped entirely.
      answers.size = formData.get('size') || 'no-preference';
      // Firmness now allows multiple picks. An empty array from getAll()
      // reads the same as "no preference" everywhere it's scored, so there's
      // no separate fallback needed the way the single-choice fields have.
      answers.firmness = formData.getAll('firmness');
      answers.material = formData.getAll('material');
      answers.intendedUse = formData.getAll('intendedUse');
      answers.texture = formData.getAll('texture');
      answers.toyType = formData.getAll('toyType');

      /* Must-haves are one flat list of toggles rather than four radio groups,
         so these four scoring fields are derived from a single checkbox list.
         Not selecting something means no preference, which is exactly what
         the old "Doesn't matter" defaults produced — the scoring sees no
         difference for anyone who skips the step.

         The values are internal keys chosen to read clearly in the markup;
         they are mapped onto the scoring vocabulary here and nowhere else. */
      var mustHave = formData.getAll('mustHave');
      var wants = function (key) { return mustHave.indexOf(key) > -1; };

      answers.flaredBase = wants('flaredBase') ? 'required' : 'no-preference';
      answers.harness = wants('harness') ? 'yes' : 'no-preference';
      answers.waterproof = wants('waterproof') ? 'yes' : 'no-preference';

      /* Vibration keeps three states, which is why it is two toggles rather
         than one. "Non-vibrating" is a hard filter in computeScore — it
         removes vibrating products from the results entirely rather than
         ranking them lower — so folding it into a single "Vibrating" toggle
         would have quietly dropped a real capability.

         Selecting both is a contradiction. Resolving it to no preference is
         deliberate: taking either side literally would either eliminate the
         whole catalogue or silently ignore half of what was asked for. */
      if (wants('vibrating') && wants('nonVibrating')) {
        answers.vibration = 'no-preference';
      } else if (wants('vibrating')) {
        answers.vibration = 'yes';
      } else if (wants('nonVibrating')) {
        answers.vibration = 'no';
      } else {
        answers.vibration = 'no-preference';
      }

      return answers;
    }

    function renderCard(item, index, maxScore) {
      var a = document.createElement('a');
      a.className = 'toymatch-quiz__card';
      // The campaign tag rides on every outbound click here — this is the
      // only place a visitor leaves the quiz for a product page.
      a.href = item.productUrl + '?variant=' + item.variantId + '&' + CAMPAIGN_UTM;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      // Cards fade up in sequence rather than all at once.
      a.style.animationDelay = Math.min(index, 8) * 60 + 'ms';

      // Only worth showing once there's enough to measure against. Below the
      // threshold the maths is technically correct but useless — answer only
      // "experience level" and every Beginner product ties on 100%, which
      // reads as a broken promise rather than a recommendation.
      var matchHtml = '';
      if (maxScore >= MATCH_BADGE_MIN_SCORE) {
        var pct = Math.round((item.score / maxScore) * 100);
        if (pct > 100) pct = 100;
        if (pct > 0) {
          matchHtml = '<span class="toymatch-quiz__match' +
            (pct >= 75 ? ' is-strong' : '') + '">' + pct + '% match</span>';
        }
      }

      var tagsHtml = (item.reasons || []).slice(0, 3).map(function (r) {
        return '<span class="toymatch-quiz__card-tag">' + escapeHtml(r) + '</span>';
      }).join('');

      var lengthStr = formatDimension(item.insertableLengthCm);
      var diameterStr = formatDimension(item.maxDiameterCm);
      var dimRows = '';
      if (lengthStr) {
        dimRows += '<div class="toymatch-quiz__dim-row"><span class="toymatch-quiz__dim-label">Length</span><span class="toymatch-quiz__dim-value">' + escapeHtml(lengthStr) + '</span></div>';
      }
      if (diameterStr) {
        dimRows += '<div class="toymatch-quiz__dim-row"><span class="toymatch-quiz__dim-label">Width</span><span class="toymatch-quiz__dim-value">' + escapeHtml(diameterStr) + '</span></div>';
      }
      var dimsHtml = dimRows ? '<div class="toymatch-quiz__dims">' + dimRows + '</div>' : '';

      a.innerHTML =
        '<span class="toymatch-quiz__card-media">' +
          '<img class="toymatch-quiz__card-image" src="' + escapeHtml(item.image) + '" loading="lazy" alt="' + escapeHtml(item.productTitle) + '">' +
          matchHtml +
        '</span>' +
        '<p class="toymatch-quiz__card-title">' + escapeHtml(item.productTitle) + '</p>' +
        '<p class="toymatch-quiz__card-price">' + money(item.price) + '</p>' +
        dimsHtml +
        '<div class="toymatch-quiz__card-tags">' + tagsHtml + '</div>';

      return a;
    }

    function renderResults() {
      var answers = collectAnswers();
      var results = getResults(variants, answers);
      var top = results.filter(function (r) { return r.score > 0; });
      var heading = root.querySelector('[data-quiz-results-heading]');

      // The name field is optional, so this is empty far more often than not
      // and every heading below has to read correctly without it.
      var who = possessiveName(answers.firstName);

      if (top.length >= 3) {
        if (heading) heading.textContent = who ? who + ' matches' : 'Your matches';
      } else {
        top = results.slice(0, 6);
        if (heading) heading.textContent = who ? who + ' closest matches' : 'Closest matches';
      }

      gridEl.innerHTML = '';
      if (!top.length) {
        var empty = document.createElement('p');
        empty.className = 'toymatch-quiz__empty';
        empty.textContent = "We couldn't find a match for that combination — try loosening a preference or two.";
        gridEl.appendChild(empty);
        announce('No matches found. Try loosening a preference.');
        return;
      }

      var maxScore = maxAchievableScore(answers);
      top.forEach(function (item, i) {
        gridEl.appendChild(renderCard(item, i, maxScore));
      });
      announce(top.length + ' matches found.');
    }

    function goToQuiz() {
      introEl.hidden = true;
      formEl.hidden = false;
      resultsEl.hidden = true;
      loadingEl.hidden = true;
      // Runs on both the first Start and every retake, so a retake can't
      // leave a step stuck on whatever was picked last time — including the
      // bug this whole change fixes, where a previous run's selection stayed
      // checked and, with Next hidden, left no way to move past that step.
      resetSingleChoiceSteps();
      // Re-enter on retake, since results released the takeover and stopped
      // the background drifting.
      enterFullscreen();
      enterDark();
      currentStep = 0;
      showStep(0, 'forward');
    }

    function finishQuiz() {
      formEl.hidden = true;
      loadingEl.hidden = false;

      var messages = pickTwoMessages();
      if (loadingMessageEl) {
        loadingMessageEl.textContent = messages[0];
        loadingMessageEl.classList.remove('is-fading');
      }

      window.setTimeout(function () {
        if (loadingMessageEl) {
          loadingMessageEl.classList.add('is-fading');
          window.setTimeout(function () {
            loadingMessageEl.textContent = messages[1];
            loadingMessageEl.classList.remove('is-fading');
          }, 200);
        }
      }, 3000);

      window.setTimeout(function () {
        renderResults();
        loadingEl.hidden = true;
        resultsEl.hidden = false;
        // Hand the page back so results scroll normally and the footer is
        // reachable again. Dark mode stays on through the results, but the
        // background stops drifting — the search is over.
        exitFullscreen();
        settleBackground();
        // scroll-margin-top on the section keeps this clear of the theme's
        // sticky header; without it the results heading lands underneath it.
        root.scrollIntoView({ block: 'start' });
      }, 6000);
    }

    if (startBtn) {
      startBtn.addEventListener('click', function () {
        // Normally the catalogue has already loaded and this resolves on the
        // spot. If the visitor is quicker than the network, hold the intro
        // until the questions are ready rather than showing an empty step.
        if (optionsBuilt) {
          goToQuiz();
          return;
        }
        startBtn.disabled = true;
        catalogueReady.then(function () {
          startBtn.disabled = false;
          buildOptionsAndSteps();
          goToQuiz();
        });
      });
    }

    function goNext() {
      if (currentStep < steps.length - 1) {
        currentStep += 1;
        showStep(currentStep, 'forward');
      }
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', goNext);
    }

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        if (currentStep > 0) {
          currentStep -= 1;
          showStep(currentStep, 'back');
        }
      });
    }

    // Auto-advance. Delegated from the form so it also covers the option lists
    // that get injected after load. The short delay lets the visitor see their
    // choice register before the step changes.
    var advanceTimer = null;
    if (formEl) {
      formEl.addEventListener('change', function (e) {
        if (!e.target || e.target.type !== 'radio') {
          if (e.target && e.target.type === 'checkbox') tap();
          return;
        }
        tap();
        var stepEl = e.target.closest('[data-quiz-step]');
        if (!stepEl || !isSingleChoiceStep(stepEl)) return;
        if (steps.indexOf(stepEl) !== currentStep) return;
        if (currentStep >= steps.length - 1) return;
        window.clearTimeout(advanceTimer);
        advanceTimer = window.setTimeout(goNext, 280);
      });

      // Adding a text field turned this into a form a browser will happily
      // submit: a single text input and an Enter press triggers implicit
      // submission, which navigates the page and throws away every answer.
      // Nothing here is ever meant to submit — the nav buttons are all
      // type="button" and there is no action — so the submit event is
      // swallowed outright.
      formEl.addEventListener('submit', function (e) {
        e.preventDefault();
      });

      // With submission blocked, Enter would otherwise do nothing at all,
      // which feels broken while typing a name. Treat it as pressing Next.
      formEl.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter') return;
        if (!e.target || e.target.tagName !== 'INPUT' || e.target.type !== 'text') return;
        e.preventDefault();
        if (currentStep < steps.length - 1) {
          goNext();
        } else {
          finishQuiz();
        }
      });
    }

    if (finishBtn) {
      finishBtn.addEventListener('click', finishQuiz);
    }

    // The intro skip link was removed from the markup; this stays guarded so
    // the script keeps working if it's ever reinstated.
    if (skipLink) {
      skipLink.addEventListener('click', function (e) {
        e.preventDefault();
        exitFullscreen();
        exitDark();
        root.hidden = true;
        if (nativeGrid) nativeGrid.style.display = 'block';
        if (nativeBanner) nativeBanner.style.display = 'block';
      });
    }

    // "Show all products instead" was removed from the results screen. Same
    // arrangement as the skip link: guarded, so putting the markup back is
    // all it would take to bring it working.
    if (showAllLink) {
      showAllLink.addEventListener('click', function (e) {
        e.preventDefault();
        exitFullscreen();
        exitDark();
        root.hidden = true;
        if (nativeGrid) nativeGrid.style.display = 'block';
        if (nativeBanner) nativeBanner.style.display = 'block';
      });
    }

    if (retakeBtn) {
      retakeBtn.addEventListener('click', function () {
        resultsEl.hidden = true;
        goToQuiz();
      });
    }
  }

  function init() {
    document.querySelectorAll('.toymatch-quiz').forEach(initQuiz);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
